import {
  Button,
  CustomFilter,
  DatePicker,
  Form,
  message,
  Table,
  Tag,
  useBreadcrumbRoutes,
  Wrapper,
} from '@maxtropy/components';
import {
  apiV2OperationDataReportPost,
  V2OperationDataReportPostRequest,
  V2OperationDataReportPostResponse,
} from '@maxtropy/dmes-apis-v2';
import { useRequest } from 'ahooks';
import { Flex } from 'antd';
import { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import qs from 'qs';
import { useMemo, useState } from 'react';

interface FilterParams {
  monthRange: [Dayjs, Dayjs];
}

type OperationDataReportRecord = Exclude<V2OperationDataReportPostResponse['records'], undefined>[number];
type SearchParams = Required<Pick<V2OperationDataReportPostRequest, 'from' | 'to'>>;

const MONTH_FORMAT = 'YYYY-MM';
const MAX_MONTH_SPAN = 35;

const formatValue = (value?: number) => (value == null ? '-' : value.toFixed(2));
const formatRate = (value?: number) => (value == null ? '-' : `${value.toFixed(2)}%`);

const columns: ColumnsType<OperationDataReportRecord> = [
  {
    title: '月份',
    dataIndex: 'month',
    width: 110,
    fixed: 'left',
    render: value => value || '-',
  },
  {
    title: '运行特性',
    dataIndex: 'operationCharacteristic',
    width: 110,
    fixed: 'left',
    render: value =>
      value ? (
        <Tag border="solid" color={value === 'MAINTENANCE_MONTH' ? 'warning' : 'success'}>
          {value === 'MAINTENANCE_MONTH' ? '检修月' : '非检修月'}
        </Tag>
      ) : (
        '-'
      ),
  },
  { title: '火电(MWh)', dataIndex: 'thermalGeneration', width: 120, render: formatValue },
  { title: '光伏(MWh)', dataIndex: 'pvGeneration', width: 120, render: formatValue },
  { title: '弃光(MWh)', dataIndex: 'curtailmentAmount', width: 120, render: formatValue },
  { title: '弃光率', dataIndex: 'curtailmentRate', width: 100, render: formatRate },
  { title: '市电(MWh)', dataIndex: 'gridPowerConsumption', width: 120, render: formatValue },
  { title: '需量(MW)', dataIndex: 'demand', width: 110, render: formatValue },
  { title: '总负荷(MWh)', dataIndex: 'totalLoadConsumption', width: 130, render: formatValue },
  {
    title: '蒙西冶炼用电量(MWh)',
    dataIndex: 'mengxiSmeltingConsumption',
    width: 180,
    render: formatValue,
  },
  { title: '化工用电量(MWh)', dataIndex: 'chemicalConsumption', width: 160, render: formatValue },
  { title: '调度总次数', dataIndex: 'totalDispatchCount', width: 120, render: () => '-' },
  { title: '火电调度次数', dataIndex: 'thermalDispatchCount', width: 130, render: () => '-' },
  { title: '光伏调度次数', dataIndex: 'pvDispatchCount', width: 130, render: () => '-' },
  { title: '负荷调度次数', dataIndex: 'loadDispatchCount', width: 130, render: () => '-' },
];

const getDefaultMonthRange = (): [Dayjs, Dayjs] => {
  const currentMonth = dayjs().startOf('month');
  return [currentMonth.subtract(11, 'month'), currentMonth];
};

const toSearchParams = ([from, to]: [Dayjs, Dayjs]): SearchParams => ({
  from: String(from.startOf('month').valueOf()),
  to: String(to.endOf('month').valueOf()),
});

const OperationDataReport = () => {
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const [form] = Form.useForm<FilterParams>();
  const defaultMonthRange = useMemo(getDefaultMonthRange, []);
  const defaultSearchParams = useMemo(() => toSearchParams(defaultMonthRange), [defaultMonthRange]);
  const [searchParams, setSearchParams] = useState<SearchParams>(defaultSearchParams);

  const { data = [], loading } = useRequest(
    async () => {
      // timestamp 由公共请求层统一注入，接口生成类型尚未将该字段标记为可选。
      const response = await apiV2OperationDataReportPost(searchParams as V2OperationDataReportPostRequest);
      return response.records ?? [];
    },
    { refreshDeps: [searchParams] }
  );

  const onFinish = ({ monthRange }: FilterParams) => {
    if (monthRange[1].diff(monthRange[0], 'month') > MAX_MONTH_SPAN) {
      message.warning('时间范围不能超过36个月');
      return;
    }
    setSearchParams(toSearchParams(monthRange));
  };

  const onReset = () => {
    form.setFieldsValue({ monthRange: defaultMonthRange });
    setSearchParams(defaultSearchParams);
  };

  const handleExport = () => {
    window.open(`/api/v2/operation-data-report/export?${qs.stringify(searchParams, { indices: false })}`, '_blank');
  };

  const filters = (
    <CustomFilter<FilterParams>
      form={form}
      initialValues={{ monthRange: defaultMonthRange }}
      onFinish={onFinish}
      onReset={onReset}
    >
      <Form.Item name="monthRange" label="时间范围">
        <DatePicker.RangePicker
          picker="month"
          format={MONTH_FORMAT}
          allowClear={false}
          placeholder={['开始月份', '结束月份']}
          style={{ width: '100%' }}
          disabledDate={(current, { from }) => {
            if (!from) return false;
            return Math.abs(current.startOf('month').diff(from.startOf('month'), 'month')) > MAX_MONTH_SPAN;
          }}
        />
      </Form.Item>
    </CustomFilter>
  );

  return (
    <Wrapper routes={breadcrumbRoutes?.routes} filters={filters} className="page_wrapper">
      <Flex justify="flex-end" style={{ marginBottom: 16 }}>
        <Button type="primary" onClick={handleExport}>
          导出
        </Button>
      </Flex>
      <Table<OperationDataReportRecord>
        rowKey={record => record.month ?? ''}
        sticky
        loading={loading}
        columns={columns}
        dataSource={data}
        pagination={false}
        scroll={{ x: 1840 }}
      />
    </Wrapper>
  );
};

export default OperationDataReport;
